"use client";

import { useState, useRef, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FileUp, Sparkles, PenTool } from "lucide-react";
import { TEMPLATE_METADATA, loadTemplate } from "@/lib/templates-index";
import { CreateTierListForm } from "@/components/create-tier-list-form";
import { ImportTemplateDialog } from "@/components/import-template-dialog";
import { parseTemplateFile, importTierListTemplate } from "@/lib/template-utils";
import { upgradeTemplate, validateTemplate } from "@/lib/template-versioning";
import { TierListTemplate } from "@/types/tier-list";
import { setLastUsedTierListId } from "@/lib/indexdb";

type CreationMode = "scratch" | "template" | "import";
type WizardStep = "select-mode" | "create-form" | "select-template";

interface TierListCreationWizardProps {
  onSuccess: (tierListId: string) => void;
  onCancel?: () => void;
}

export function TierListCreationWizard({
  onSuccess,
  onCancel,
}: TierListCreationWizardProps) {
  const [step, setStep] = useState<WizardStep>("select-mode");
  const [mode, setMode] = useState<CreationMode | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [importedTemplate, setImportedTemplate] = useState<TierListTemplate | null>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const importFileInputRef = useRef<HTMLInputElement>(null);

  // Group templates by their groupName (derived from name field)
  // Must be at top level, not inside conditional
  const groupedTemplates = useMemo(() => {
    const groups: Record<string, typeof TEMPLATE_METADATA> = {};

    // First pass: group by groupName from metadata
    TEMPLATE_METADATA.forEach((template) => {
      const group = template.groupName || 'General';

      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(template);
    });

    // Second pass: move single-item groups to General
    const finalGroups: Record<string, typeof TEMPLATE_METADATA> = { General: groups.General || [] };

    Object.entries(groups).forEach(([groupName, templates]) => {
      if (groupName === 'General') return; // Already handled

      if (templates.length === 1) {
        // Single item, move to General
        finalGroups.General.push(...templates);
      } else {
        // Multiple items, keep as separate group
        finalGroups[groupName] = templates;
      }
    });

    // Sort groups: "General" first, then alphabetically
    const sortedGroups = Object.entries(finalGroups).sort(([a], [b]) => {
      if (a === 'General') return -1;
      if (b === 'General') return 1;
      return a.localeCompare(b);
    });

    return sortedGroups;
  }, []);

  const handleModeSelect = (selectedMode: CreationMode) => {
    setMode(selectedMode);
    setErrorMessage(""); // Clear any previous errors

    if (selectedMode === "scratch") {
      setStep("create-form");
    } else if (selectedMode === "template") {
      setStep("select-template");
    } else if (selectedMode === "import") {
      // Trigger file picker
      importFileInputRef.current?.click();
    }
  };

  const handleTemplateUse = async (templateId: string) => {
    setErrorMessage("");
    try {
      // Load full template (with any items/images if present)
      const template = await loadTemplate(templateId);

      // Import template directly and go to app
      const tierListId = await importTierListTemplate(template, true);
      await setLastUsedTierListId(tierListId);
      onSuccess(tierListId);
    } catch (error) {
      console.error("Failed to create tier list from template:", error);
      setErrorMessage("Failed to create tier list from template. Please try again.");
    }
  };

  const handleTemplateEdit = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setStep("create-form");
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setErrorMessage("");
    try {
      const file = files[0];
      let template = await parseTemplateFile(file);

      // Validate template
      if (!validateTemplate(template)) {
        setErrorMessage("Invalid template file. Please check the format and try again.");
        return;
      }

      // Upgrade template if needed
      template = upgradeTemplate(template);

      setImportedTemplate(template);

      // Check if template has items with tier placement
      const hasPlacedItems = template.items.some((item) => item.tierId);

      if (hasPlacedItems) {
        // Show dialog to ask user what to do
        setShowImportDialog(true);
      } else {
        // No placed items, just import directly
        await handleImportTemplate(template, false);
      }
    } catch (error) {
      console.error("Failed to import template:", error);
      setErrorMessage("Failed to import template. Please check the file and try again.");
    } finally {
      // Reset file input
      if (importFileInputRef.current) {
        importFileInputRef.current.value = "";
      }
    }
  };

  const handleImportTemplate = async (
    template: TierListTemplate,
    moveToSidebar: boolean
  ) => {
    setErrorMessage("");
    try {
      // If moving to sidebar, clear all tier placements
      if (moveToSidebar) {
        template = {
          ...template,
          items: template.items.map((item) => ({
            ...item,
            tierId: undefined,
            order: undefined,
          })),
        };
      }

      // Import the template
      const tierListId = await importTierListTemplate(template, true);
      await setLastUsedTierListId(tierListId);
      onSuccess(tierListId);
    } catch (error) {
      console.error("Failed to create tier list from template:", error);
      setErrorMessage("Failed to create tier list from template. Please try again.");
    }
  };

  // Step 1: Mode Selection
  if (step === "select-mode") {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold">Create a Tier List</h2>
          <p className="text-muted-foreground">
            How would you like to get started?
          </p>
        </div>

        {errorMessage && (
          <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
            <p className="text-sm text-destructive">{errorMessage}</p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4">
          <button
            onClick={() => handleModeSelect("scratch")}
            className="group p-6 border-2 border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-all text-left"
          >
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                <PenTool className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-1">Start from Scratch</h3>
                <p className="text-sm text-muted-foreground">
                  Create a custom tier list with your own tiers and configuration
                </p>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleModeSelect("template")}
            className="group p-6 border-2 border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-all text-left"
          >
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                <Sparkles className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-1">Use a Template</h3>
                <p className="text-sm text-muted-foreground">
                  Start with one of our preconfigured templates for common use cases
                </p>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleModeSelect("import")}
            className="group p-6 border-2 border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-all text-left"
          >
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                <FileUp className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-1">Import a Template</h3>
                <p className="text-sm text-muted-foreground">
                  Upload a previously exported template file (JSON)
                </p>
              </div>
            </div>
          </button>
        </div>

        {onCancel && (
          <div className="flex justify-end">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        )}

        {/* Hidden file input for import */}
        <input
          ref={importFileInputRef}
          type="file"
          accept=".json"
          className="hidden"
          onChange={handleImportFile}
        />

        {/* Import Template Dialog */}
        <ImportTemplateDialog
          isOpen={showImportDialog}
          onClose={() => {
            setShowImportDialog(false);
            setImportedTemplate(null);
          }}
          onKeepExisting={async () => {
            if (importedTemplate) {
              await handleImportTemplate(importedTemplate, false);
            }
            setShowImportDialog(false);
          }}
          onUseTemplate={async () => {
            if (importedTemplate) {
              await handleImportTemplate(importedTemplate, true);
            }
            setShowImportDialog(false);
          }}
          templateName={importedTemplate?.name || ""}
          hasExistingItems={
            importedTemplate?.items.some((item) => item.tierId) || false
          }
        />
      </div>
    );
  }

  // Step 2a: Template Selection
  if (step === "select-template") {
    const renderTemplate = (template: typeof TEMPLATE_METADATA[0]) => (
      <div
        key={template.id}
        className="p-4 bg-card rounded-lg shadow-sm hover:shadow-md transition-shadow"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="font-semibold text-lg">
              {template.displayName}
            </h3>
            {template.description && (
              <p className="text-sm text-muted-foreground mb-3">
                {template.description}
              </p>
            )}
            <div className="flex flex-wrap gap-1.5">
              {template.tiers.slice(0, 7).map((tier) => (
                <div
                  key={tier.id}
                  className="px-2 py-1 rounded text-xs font-semibold shadow-sm"
                  style={{
                    backgroundColor: tier.color.background,
                    color: tier.color.label,
                  }}
                >
                  {tier.label}
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Button
              onClick={() => handleTemplateUse(template.id)}
              size="sm"
            >
              Use
            </Button>
            <Button
              onClick={() => handleTemplateEdit(template.id)}
              variant="outline"
              size="sm"
            >
              Edit
            </Button>
          </div>
        </div>
      </div>
    );

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Choose a Template</h2>
            <p className="text-muted-foreground">
              Select a template to get started quickly
            </p>
          </div>
          <Button variant="outline" onClick={() => setStep("select-mode")}>
            Back
          </Button>
        </div>

        {errorMessage && (
          <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
            <p className="text-sm text-destructive">{errorMessage}</p>
          </div>
        )}

        <div className="max-h-[60vh] overflow-y-auto pr-2">
          <Accordion type="multiple" className="space-y-3">
            {groupedTemplates.map(([groupName, templates]) => (
              <AccordionItem key={groupName} value={groupName} className="border rounded-lg">
                <AccordionTrigger className="hover:no-underline px-4 py-3 hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{groupName}</span>
                    <span className="text-sm text-muted-foreground">
                      ({templates.length} {templates.length === 1 ? 'template' : 'templates'})
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="grid grid-cols-1 gap-3 pt-3">
                    {templates.map(renderTemplate)}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    );
  }

  // Step 2b: Create Form (for scratch or editing template)
  if (step === "create-form") {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">
              {mode === "scratch" ? "Create Your Tier List" : "Customize Template"}
            </h2>
            <p className="text-muted-foreground">
              {mode === "scratch"
                ? "Configure your tier list from scratch"
                : "Edit the template to fit your needs"}
            </p>
          </div>
          <Button variant="outline" onClick={() => {
            if (mode === "template") {
              setStep("select-template");
            } else {
              setStep("select-mode");
            }
          }}>
            Back
          </Button>
        </div>

        <CreateTierListForm
          onSuccess={onSuccess}
          startFromScratch={mode === "scratch"}
          templateId={selectedTemplateId}
        />
      </div>
    );
  }

  return null;
}
